/**
 *
 * Organizations/Show/Events/Table
 *
 */

import React, { memo, useState, useCallback, useMemo } from 'react'

// Libraries
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { List as RVList, AutoSizer } from 'react-virtualized'

// Components
import Checkbox from 'components/Checkbox'
import DropDownMenu from 'components/DropDownMenu'
import Table from 'components/Table'
import RegistrationsChartWidget from 'components/Organizations/Show/Events/RegistrationsChartWidget'

// GraphQl
import { gql } from '@apollo/client'
import useQuery from 'components/UseQuery'

// Utils
import { toLocalDate } from 'components/utils'
import { useHandleInputChangeWithDelay } from 'hooks'
import { COLORS } from 'global-styles'

const TableContainer = styled.div`
	.tbody {
		.td {
			padding: 1.3rem 1.5rem;

			&.header-cell {
				overflow: show !important;
			}
		}

		.empty-td {
			height: 23px !important;
		}
	}

	.thead {
		.td.header-cell {
			overflow: visible !important;
		}
	}
`

const CustomSelect = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	border: 1px solid ${COLORS.borderGrey};
	width: 100%;
	margin-top: 5px;
	padding: 3px 10px;
	font-weight: normal;
	color: ${COLORS.dark};
`

const CustomInput = styled.div`
	position: relative;

	input {
		padding: 10px 10px 10px 35px !important;
		height: 35px;
		font-size: 0.95rem;
		width: 100%;
		border-bottom: 1px solid ${COLORS.grey_e};
		border-top: 0;
		border-left: 0;
		border-right: 0;

		&:focus {
			outline: none !important;
			box-shadow: none;
			border-color: ${COLORS.primary};
		}

		&.error {
			outline-color: ${COLORS.danger} !important;
			border-color: ${COLORS.danger} !important;
		}
	}

	.search {
		position: absolute;
		top: 11px;
		left: 12px;
	}

	.close {
		position: absolute;
		font-size: 1rem;
		top: 10px;
		right: 8px;
		cursor: pointer;
	}
`

const ListContainer = styled.div`
	.rv-row {
		padding: 7px 10px;
		border-bottom: 1px solid ${COLORS.grey_e};
		color: ${COLORS.dark};
		font-weight: normal;
		cursor: pointer;

		display: flex;
		align-items: center;

		span {
			margin-top: 2px;
		}

		&:hover {
			background-color: ${COLORS.grey_e};
		}

		&.selected {
			background-color: ${COLORS.lightGrey};
		}
	}

	&::-webkit-scrollbar {
		width: 2px;
		height: 0;
	}

	&::-webkit-scrollbar,
	&::-webkit-scrollbar-track,
	&::-webkit-scrollbar-thumb {
		background: transparent;
	}

	&:hover {
		&::-webkit-scrollbar-track {
			background: #ddd;
		}

		&::-webkit-scrollbar-thumb {
			background: #665;
		}
	}

	label {
		margin: 0;
		transform: scale(0.9);
	}
`

const ListControllers = styled.div`
	.btn {
		padding: 2px 7px;
		font-size: 0.85rem;
	}
`

const SEARCH_EVENTS = gql`
	query searchOrganizationEvents(
		$organization_id: ID!
		$page: Int
		$page_size: Int
		$sort: String
		$sort_direction: String
		$filters: JSON
		$topic_type: String
		$user_ids: [String]
		$start_date: ISO8601DateTime
		$end_date: ISO8601DateTime
	) {
		search: search_organization_events(
			organization_id: $organization_id
			page: $page
			page_size: $page_size
			sort: $sort
			sort_direction: $sort_direction
			filters: $filters
			topic_type: $topic_type
			start_date: $start_date
			end_date: $end_date
			user_ids: $user_ids
		) {
			_total
			registrations_list
			events {
				id
				venue
				start_time
				capacity
				registrations_count
				confirmation_confirmed_count
				attended_count
				campaign_id
				user {
					id
					full_name
				}
				campaign {
					id
					topic_name
					is_meal_event
				}
				address_components {
					state
				}
			}
		}
	}
`

function UsersVirtualList({ users = [], user_ids = [], toggleSelectedUser }) {
	const rowCount = users.length
	const rowHeight = 40

	const rowRenderer = useCallback(
		({ index, key, style }) => {
			const user = users[index]
			const selected = user_ids.includes(user.id)

			return (
				<div
					key={key}
					style={style}
					className={`rv-row ${selected ? 'selected' : ''}`}
					onClick={(e) => {
						e.preventDefault()
						e.stopPropagation()
						toggleSelectedUser(user.id)
					}}
					role="listitem"
				>
					<Checkbox checked={selected} />
					<span className="rv-row-text">{user.full_name}</span>
				</div>
			)
		},
		[users, toggleSelectedUser],
	)

	return (
		<ListContainer style={{ width: '350px' }}>
			<AutoSizer disableHeight>
				{({ width: autoWidth }) => (
					<RVList
						width={autoWidth}
						height={250}
						rowCount={rowCount}
						rowHeight={rowHeight}
						overscanRowCount={8}
						rowRenderer={rowRenderer}
						tabIndex={-1}
					/>
				)}
			</AutoSizer>
		</ListContainer>
	)
}

function EventsTable({
	organization_id,
	filters: { start_date, end_date, user_ids, topic_type },
	setUserIds,
	users = [],
}) {
	const [search, setSearch] = useState('')

	const searched_users = useMemo(() => {
		if (search === '') return users

		return users.filter((u) => u.full_name.toLowerCase().includes(search.toLowerCase()))
	}, [users, search])

	const toggleSelectedUser = (user_id) => {
		if (!user_ids.includes(user_id)) {
			setUserIds([...user_ids, user_id])

			return
		}

		setUserIds(user_ids.filter((item) => item !== user_id))
	}

	const columns = [
		{
			header: 'Event Venue',
			id: 'venue',
			accessor: (r) => (
				<Link to={`/campaigns/${r.campaign.id}/events/${r.id}`} target="_blank">
					{r.venue}
				</Link>
			),
			filter_key: 'venue',
			minWidth: 300,
		},
		{
			header: (
				<React.Fragment>
					<span>Advisor</span>

					<div className="w-100">
						<DropDownMenu
							Button={
								<CustomSelect>
									<span>
										{user_ids.length === 0
											? 'All Users'
											: `${user_ids.length} User${user_ids.length === 1 ? '' : 's'} Selected`}
									</span>
									<FontAwesomeIcon icon="chevron-down" size="xs" fixedWidth />
								</CustomSelect>
							}
							button_style={{ width: '100%', textDecoration: 'none' }}
							menu_style={{ padding: '1px 0' }}
						>
							<div>
								<CustomInput>
									<input
										value={search}
										className={`form-control `}
										placeholder="Search Advisor"
										onClick={(e) => {
											e.stopPropagation()
										}}
										onChange={(e) => {
											setSearch(e.target.value)
										}}
									/>

									<FontAwesomeIcon icon="search" size="sm" className="search" />

									{search.length > 0 && (
										<FontAwesomeIcon
											icon="times"
											className="close"
											onClick={(e) => {
												setSearch('')
												e.stopPropagation()
											}}
										/>
									)}
								</CustomInput>
								<UsersVirtualList
									users={searched_users}
									user_ids={user_ids}
									toggleSelectedUser={toggleSelectedUser}
								/>
							</div>
							<ListControllers className="p-2">
								<button
									onClick={(e) => {
										e.stopPropagation()
										setUserIds([])
									}}
									className="btn btn-purple"
								>
									Reset Filter
								</button>
							</ListControllers>
						</DropDownMenu>
					</div>
				</React.Fragment>
			),
			accessor: 'user.full_name',
			sortable: false,
			minWidth: 225,
		},
		{
			header: 'Topic',
			id: 'topic',
			accessor: 'campaign.topic_name',
			className: 'text-center',
			filter_key: 'topic',
		},
		{
			header: 'Start Date',
			id: 'start_time',
			accessor: (r) =>
				r.start_time ? (
					<React.Fragment>{format(toLocalDate(r.start_time), 'MMM do, yyyy h:mma')}</React.Fragment>
				) : (
					'--'
				),
			className: 'text-center',
		},
		{
			header: 'Meal Event',
			id: 'is_meal',
			accessor: (r) => <React.Fragment>{r.campaign?.is_meal_event ? 'Meal' : 'Non-Meal'}</React.Fragment>,
			filter_key: 'is_meal',
			filter_options: (
				<React.Fragment>
					<option value="">All</option>
					<option value="meal">Meal</option>
					<option value="no-meal">Non-Meal</option>
				</React.Fragment>
			),
			sort_key: 'registered',
			className: 'text-center',
		},
		{
			header: 'Registrations',
			accessor: 'registrations_count',
			sort_key: 'registered',
			className: 'text-center',
		},
		{
			header: 'Confirmed',
			accessor: 'confirmation_confirmed_count',
			sort_key: 'confirmed',
			className: 'text-center',
		},
		{
			header: 'Attended',
			accessor: 'attended_count',
			sort_key: 'attended',
			className: 'text-center',
		},
		{
			header: 'Capacity',
			accessor: 'capacity',
			sort_key: 'capacity',
			className: 'text-center',
		},
		{
			header: 'State',
			id: 'state',
			accessor: (r) => r.address_components?.state,
			className: 'text-center',
			width: 0.05 * 1850,
			sortable: false,
			filter_key: 'state',
		},
	]

	const [page_size, setPageSize] = useState(10)
	const [page, setPage] = useState(1)
	const [sort, setSort] = useState({ key: 'updated_at', direction: 'desc' })
	const [filters, filter_input_states, handleFilterInputChange] = useHandleInputChangeWithDelay(
		columns.reduce((filter_object, column) => {
			if (column.filter_key) {
				filter_object[column.filter_key] = ''
			}
			return filter_object
		}, {}) || {},
	)

	const { data, loading, refetch } = useQuery(SEARCH_EVENTS, {
		variables: {
			organization_id,
			start_date,
			end_date,
			user_ids,
			topic_type,
			page,
			page_size,
			sort: sort.key,
			sort_direction: sort.direction,
			search,
			filters,
		},
		channels: [organization_id],
		default_data: {
			search: {
				events: [],
				registrations_list: [],
			},
		},
		fetchPolicy: 'network-only',
	})

	return (
		<div>
			<RegistrationsChartWidget data={data.search.registrations_list} users={users} user_ids={user_ids} />

			<TableContainer>
				<Table
					columns={columns}
					data={data.search.events}
					loading={loading}
					sort={sort}
					setSort={setSort}
					page={page}
					setPage={setPage}
					page_size={page_size}
					setPageSize={setPageSize}
					refetch={refetch}
					total_records={data.search.total}
					filter_input_states={filter_input_states}
					handleFilterInputChange={handleFilterInputChange}
				/>
			</TableContainer>
		</div>
	)
}

EventsTable.propTypes = {
	organization_id: PropTypes.string,
	filters: PropTypes.object,
	setUserIds: PropTypes.func,
	users: PropTypes.array,
}

export default memo(EventsTable)
